import type { UserOrganization } from "@server/types/responseTypes";
import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useOrganizationStore } from "@/store/organization";

/**
 * Cancels stale organization reads, then writes the activated org into the
 * zustand store and react-query cache before navigation renders. Publication
 * is skipped if the expected organization is no longer selected across that
 * asynchronous boundary. Invalidate-only would race RequireOrganization, and
 * setOrganization would trigger a spurious WorkOS org switch.
 *
 * @param queryClient - Cache client whose organization reads are fenced.
 * @param expectedOrganizationId - Selection that must remain current.
 * @param activated - Server-confirmed organization state to publish.
 */
export async function applyActivatedOrganization(
  queryClient: QueryClient,
  expectedOrganizationId: string,
  activated: UserOrganization,
): Promise<void> {
  if (
    activated.id !== expectedOrganizationId ||
    useOrganizationStore.getState().organization?.id !== expectedOrganizationId
  ) {
    return;
  }

  await queryClient.cancelQueries({ queryKey: queryKeys.organizations.all() });
  const { organization, organizations, setOrganizationState } =
    useOrganizationStore.getState();
  if (organization?.id !== expectedOrganizationId) return;

  const current = organizations ?? [];
  const nextList = current.some((org) => org.id === activated.id)
    ? current.map((org) => (org.id === activated.id ? activated : org))
    : [...current, activated];

  setOrganizationState({ organization: activated, organizations: nextList });
  queryClient.setQueryData(queryKeys.organizations.all(), nextList);
}
